import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import Modal from '../../components/Modal';
import {
    ApiToken,
    createApiToken,
    CreatedToken,
    deleteApiToken,
    getApiTokenStats,
    listApiTokens,
    revokeApiToken,
    TokenStatsResp,
    updateApiTokenRate,
} from '../../api/apitoken';
import { ApiError } from '../../api/request';
import { useCan } from '../../store';
import { apiEnvInfo } from '../../utils/apiEnv';
import ApiAccessInfo from '../../components/ApiAccessInfo';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { emitApprovalChanged } from '../../utils/approvalLabels';
import SearchSelect from '../../components/SearchSelect';

// API Token 管理页: 申请/查看(掩码)/吊销 openapi 访问令牌.
// 明文只在创建成功的弹窗里出现一次; 每人 active 上限 5 个.

// sdkSnippet 可直接粘贴的 material-client 初始化片段 (baseURL 与 token 都填好).
function sdkSnippet(baseURL: string, token: string): string {
    return `client, err := materialclient.New(\n    "${baseURL}",\n    "${token}",\n)`;
}

function fmtTime(unix: number): string {
    if (!unix) return '—';
    return new Date(unix * 1000).toLocaleString('zh-CN', { hour12: false });
}

const STATUS_FILTER_OPTS = [
    { value: 'pending', label: '待审批' },
    { value: 'active', label: '有效' },
    { value: 'revoked', label: '已吊销' },
];

function TokenList() {
    const canDelete = useCan('api_token', 'delete');
    const canManageTokens = useCan('api_token', 'update');
    // 专用域令牌的签发资格 (2026-09-14, 与后端 issueScopeDenied 同口径):
    // 签发不得授出自己没有的权限 —— sync 看站点同步编辑权, collector 看数据资产编辑权.
    const canIssueSync = useCan('datasync', 'update');
    const canIssueCollector = useCan('data_asset', 'update');
    const scopeOpts = useMemo(
        () => [
            { value: 'read', label: 'read — 普通只读 (下游数据消费)' },
            {
                value: 'manage',
                label: 'manage — 以我本人的权限发起变更 (给 material-data skill 等工具用)',
            },
            ...(canIssueSync
                ? [
                      {
                          value: 'sync',
                          label: 'sync — 站点同步专用 (美服→国服镜像, 慎用)',
                      },
                  ]
                : []),
            ...(canIssueCollector
                ? [
                      {
                          value: 'collector',
                          label: 'collector — 商品采集增量写入 (采集方专用)',
                      },
                  ]
                : []),
        ],
        [canIssueSync, canIssueCollector],
    );
    // scope 选择对所有人开放 (2026-09-24): read / manage 任何人都能给自己签 (manage 永远不超过本人权限),
    // sync / collector 仍按各自的签发资格显示.
    const canPickScope = true;
    const [list, setList] = useState<ApiToken[]>([]);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 筛选 (前端本地过滤)
    const [keyword, setKeyword] = useState('');
    const [ownerFilter, setOwnerFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    // 创建流程
    const [showCreate, setShowCreate] = useState(false);
    const [name, setName] = useState('');
    const [scope, setScope] = useState('read');
    const [busy, setBusy] = useState(false);
    // 创建成功后一次性展示明文
    const [created, setCreated] = useState<CreatedToken | null>(null);
    const [copied, setCopied] = useState(false);
    // admin 限流编辑
    const [rateEdit, setRateEdit] = useState<{
        id: number;
        qps: string;
        daily: string;
    } | null>(null);
    // 调用统计弹窗
    const [stats, setStats] = useState<TokenStatsResp | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // 请求序号: 权限异步就绪会让首屏连发 all=false / all=true 两个请求, 丢弃过期响应防止后到的覆盖
    const reqSeq = useRef(0);
    const load = useCallback(async (all: boolean) => {
        const seq = ++reqSeq.current;
        setLoading(true);
        setError('');
        try {
            const res = await listApiTokens({ all });
            if (seq !== reqSeq.current) return;
            setList(res || []);
        } catch (e) {
            if (seq !== reqSeq.current) return;
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
        } finally {
            if (seq === reqSeq.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(showAll);
    }, [load, showAll]);

    // 有令牌治理权的人默认看所有人的 (权限异步加载, false→true 时勾上; 之后手动取消不受影响)
    useEffect(() => {
        if (canManageTokens) setShowAll(true);
    }, [canManageTokens]);

    // 申请人展示名: 邮箱优先, 无邮箱回退 uid (与表格"申请人"列一致)
    const ownerLabel = (t: ApiToken) =>
        t.ownerEmail || (t.ownerUid ? String(t.ownerUid) : '—');

    // 申请人下拉选项 (从当前列表提取去重)
    const owners = useMemo(
        () => Array.from(new Set(list.map(ownerLabel))).sort(),
        [list],
    );

    const filtered = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        return list.filter((t) => {
            if (
                kw &&
                !t.name.toLowerCase().includes(kw) &&
                !t.tokenPrefix.toLowerCase().includes(kw)
            )
                return false;
            if (ownerFilter && ownerLabel(t) !== ownerFilter) return false;
            if (statusFilter && t.status !== statusFilter) return false;
            return true;
        });
    }, [list, keyword, ownerFilter, statusFilter]);

    // 批量操作 (2026-09-10): API Token 不走审批工单 (删除即时生效, 且有 owner 与"先吊销"两道校验),
    // 所以走 BatchActions 的 localDelete 模式 —— 逐条调删除接口并汇总失败.
    const sel = useBatchSelect(
        useMemo(() => filtered.map((t) => String(t.id)), [filtered]),
        [keyword, ownerFilter, statusFilter, showAll].join('|'),
    );

    const alertErr = (prefix: string, e: unknown) => {
        const msg =
            e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
        alertDialog(`${prefix}: ${msg}`);
    };

    const onCreate = async () => {
        if (!name.trim()) {
            alertDialog('请填写 token 用途 (例: xcs-api 数据同步)');
            return;
        }
        setBusy(true);
        try {
            const res = await createApiToken(name.trim(), scope);
            setShowCreate(false);
            setName('');
            setScope('read');
            setCreated(res);
            setCopied(false);
            if (res.status === 'pending') emitApprovalChanged(); // 顶栏铃铛立刻计入新工单
            await load(showAll);
        } catch (e) {
            alertErr('创建失败', e);
        } finally {
            setBusy(false);
        }
    };

    const onRevoke = async (t: ApiToken) => {
        if (
            !(await confirmDialog(
                t.status === 'pending'
                    ? `确认撤回 token「${t.name}」(${t.tokenPrefix}****) 的申请?\n撤回后即使审批通过也不会生效.`
                    : `确认吊销 token「${t.name}」(${t.tokenPrefix}****)?\n吊销后使用该 token 的调用会立即失败.`,
            ))
        )
            return;
        try {
            await revokeApiToken(t.id);
            await load(showAll);
        } catch (e) {
            alertErr('吊销失败', e);
        }
    };

    const onDelete = async (t: ApiToken) => {
        if (
            !(await confirmDialog(
                `确认删除 token 记录「${t.name}」(${t.tokenPrefix}****)?\n删除后列表不再展示, 不可恢复.`,
            ))
        )
            return;
        try {
            await deleteApiToken(t.id);
            await load(showAll);
        } catch (e) {
            alertErr('删除失败', e);
        }
    };

    const onSaveRate = async () => {
        if (!rateEdit) return;
        const qps = Number(rateEdit.qps);
        const daily = Number(rateEdit.daily);
        if (!qps || !daily || qps <= 0 || daily <= 0) {
            alertDialog('QPS / 日配额必须是正数');
            return;
        }
        setBusy(true);
        try {
            await updateApiTokenRate(rateEdit.id, {
                rateQps: qps,
                rateDaily: daily,
            });
            setRateEdit(null);
            await load(showAll);
        } catch (e) {
            alertErr('保存失败', e);
        } finally {
            setBusy(false);
        }
    };

    const openStats = async (t: ApiToken) => {
        setStatsLoading(true);
        try {
            setStats(await getApiTokenStats(t.id));
        } catch (e) {
            alertErr('统计加载失败', e);
        } finally {
            setStatsLoading(false);
        }
    };

    // OpenAPI 接入信息 (环境 + 地址): 取自构建期环境变量, 见 utils/apiEnv.
    const apiEnv = apiEnvInfo();
    const [copiedField, setCopiedField] = useState('');
    const copyText = async (field: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            window.setTimeout(() => setCopiedField(''), 1500);
        } catch {
            alertDialog('复制失败, 请手动选中复制');
        }
    };

    const copyToken = async () => {
        if (!created) return;
        try {
            await navigator.clipboard.writeText(created.token);
            setCopied(true);
        } catch {
            // clipboard API 不可用时让用户手动选中复制
            alertDialog('复制失败, 请手动选中复制');
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">API Token</div>
                    <div className="text-xs text-slate-500">
                        material-client / OpenAPI 的访问凭证 · 每人最多 5 个有效
                        token · 明文只在创建时展示一次
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {canManageTokens && (
                        <label className="text-xs text-slate-500 inline-flex items-center gap-1.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showAll}
                                onChange={(e) => {
                                    setShowAll(e.target.checked);
                                    if (!e.target.checked) setOwnerFilter('');
                                }}
                            />
                            查看所有人的
                        </label>
                    )}
                    <BatchActions
                        resource="apitoken"
                        entityLabel="Token"
                        deleteKeyField="id"
                        localDelete={(code) => deleteApiToken(Number(code))}
                        selected={sel.selectedKeys}
                        onClear={sel.clear}
                        onDone={() => load(showAll)}
                    />
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setShowCreate(true)}
                    >
                        + 申请 Token
                    </button>
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <ApiAccessInfo />

            <div className="flex items-center gap-2">
                <input
                    className="inp text-xs w-56"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索用途 / token 前缀"
                />
                {showAll && (
                    <SearchSelect
                        className="w-56"
                        value={ownerFilter}
                        onChange={(v) => setOwnerFilter(v)}
                        placeholder="全部申请人"
                        options={owners.map((o) => ({ value: o, label: o }))}
                        clearable
                    />
                )}
                <SearchSelect
                    className="text-xs w-32"
                    value={statusFilter}
                    onChange={(v) => setStatusFilter(v)}
                    placeholder="全部状态"
                    options={STATUS_FILTER_OPTS}
                    clearable
                />
                {(keyword || ownerFilter || statusFilter) && (
                    <button
                        type="button"
                        className="text-xs text-slate-500 hover:underline"
                        onClick={() => {
                            setKeyword('');
                            setOwnerFilter('');
                            setStatusFilter('');
                        }}
                    >
                        清空筛选
                    </button>
                )}
            </div>

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox hidden={!canDelete} sel={sel} />
                            </th>
                            <th className="th">用途</th>
                            <th className="th">Token</th>
                            <th className="th">申请人</th>
                            <th className="th">限流 (QPS / 日)</th>
                            <th className="th">最近使用</th>
                            <th className="th">创建时间</th>
                            <th className="th">状态</th>
                            <th className="th">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={9}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={9}
                                >
                                    {list.length === 0
                                        ? '还没有 token, 点右上角申请'
                                        : '没有符合筛选条件的 token'}
                                </td>
                            </tr>
                        )}
                        {filtered.map((t) => (
                            <tr key={t.id} className="hover:bg-slate-50">
                                <td className="td" hidden={!canDelete}>
                                    <RowCheckbox
                                        hidden={!canDelete}
                                        sel={sel}
                                        k={String(t.id)}
                                    />
                                </td>
                                <td className="td">{t.name}</td>
                                <td className="td font-mono text-xs">
                                    {t.tokenPrefix}****
                                </td>
                                <td className="td text-xs">
                                    {t.ownerEmail || t.ownerUid || '—'}
                                </td>
                                <td className="td text-xs">
                                    {rateEdit?.id === t.id ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <input
                                                className="inp text-xs w-16"
                                                value={rateEdit.qps}
                                                onChange={(e) =>
                                                    setRateEdit({
                                                        ...rateEdit,
                                                        qps: e.target.value,
                                                    })
                                                }
                                            />
                                            /
                                            <input
                                                className="inp text-xs w-24"
                                                value={rateEdit.daily}
                                                onChange={(e) =>
                                                    setRateEdit({
                                                        ...rateEdit,
                                                        daily: e.target.value,
                                                    })
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="text-xs text-brand-600 hover:underline"
                                                disabled={busy}
                                                onClick={onSaveRate}
                                            >
                                                保存
                                            </button>
                                            <button
                                                type="button"
                                                className="text-xs text-slate-500 hover:underline"
                                                onClick={() =>
                                                    setRateEdit(null)
                                                }
                                            >
                                                取消
                                            </button>
                                        </span>
                                    ) : (
                                        <>
                                            {t.rateQps} /{' '}
                                            {t.rateDaily.toLocaleString()}
                                            {canManageTokens &&
                                                t.status === 'active' && (
                                                    <button
                                                        type="button"
                                                        className="ml-2 text-xs text-brand-600 hover:underline"
                                                        onClick={() =>
                                                            setRateEdit({
                                                                id: t.id,
                                                                qps: String(
                                                                    t.rateQps,
                                                                ),
                                                                daily: String(
                                                                    t.rateDaily,
                                                                ),
                                                            })
                                                        }
                                                    >
                                                        调整
                                                    </button>
                                                )}
                                        </>
                                    )}
                                </td>
                                <td className="td text-xs">
                                    {fmtTime(t.lastUsedAt)}
                                </td>
                                <td className="td text-xs">
                                    {fmtTime(t.createdAt)}
                                </td>
                                <td className="td">
                                    {t.status === 'active' ? (
                                        <span className="tag cap-on">有效</span>
                                    ) : t.status === 'pending' ? (
                                        <span className="tag bg-amber-50 text-amber-700">
                                            待审批
                                        </span>
                                    ) : (
                                        <span className="tag bg-slate-100 text-slate-500">
                                            已吊销
                                        </span>
                                    )}
                                </td>
                                <td className="td">
                                    <button
                                        type="button"
                                        className="text-xs text-brand-600 hover:underline mr-3"
                                        disabled={statsLoading}
                                        onClick={() => openStats(t)}
                                    >
                                        统计
                                    </button>
                                    {t.status !== 'revoked' ? (
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            onClick={() => onRevoke(t)}
                                        >
                                            {t.status === 'pending'
                                                ? '撤回'
                                                : '吊销'}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            onClick={() => onDelete(t)}
                                        >
                                            删除
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 申请弹窗 */}
            <Modal
                open={showCreate}
                onClose={() => setShowCreate(false)}
                title="申请 API Token"
                width={480}
            >
                <div className="space-y-4">
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>用途说明
                        </label>
                        <input
                            className="inp"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="例: xcs-api 数据同步"
                        />
                        <div className="text-[11px] text-slate-400 mt-1">
                            写清楚给哪个系统/场景用, 方便后续审计与吊销.
                        </div>
                    </div>
                    {canPickScope && (
                        <div>
                            <label className="field-label">
                                权限域 (scope)
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={scope}
                                onChange={(v) => setScope(v)}
                                options={scopeOpts}
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                {scope === 'collector'
                                    ? 'collector 令牌只能调 /openapi/v1/collector/* 写商品采集表, 读不到其它数据; 发给采集程序 (material-collector) 使用.'
                                    : scope === 'manage'
                                      ? 'manage 令牌 = 你本人的权限去掉平台管理 (令牌/人员/权限/站点同步/审批) 和批准动作: 你能建设备它就能提"新建设备"的工单, 你只有查看权它就只能看. 主数据 (材料/设备/参数/贴士/字典/套件) 的写动作一律走审批, 令牌本身批不了任何东西; 数据资产回写 (还原图 / 商品图清洗位) 不走审批, 需你有数据资产编辑权, 调用即生效.'
                                      : scope === 'sync'
                                        ? 'sync 令牌可调 /openapi/v1/sync/* (吐原始行含软删), 仅用于国服镜像站的 SYNC_SOURCE_TOKEN, 不要发给任何下游.'
                                        : 'read 令牌 = 你本人权限的只读投影, 可调全部查询接口, 改不了任何数据; 发给下游系统用这种.'}
                            </div>
                            {scope === 'manage' && (
                                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2.5 mt-2">
                                    这是<b>"本人"级别的钥匙</b>:
                                    泄漏等于泄漏你账号的全部业务操作权
                                    (会以你的名义提工单).
                                    只放在你自己的机器上给本地工具用,
                                    不要发给任何人或系统; 用完就吊销.
                                </div>
                            )}
                        </div>
                    )}
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
                        提交后需<b>审批中心通过</b>才能使用 (明文会先显示给你,
                        通过后自动生效). 安全提醒: token 创建后
                        <b>明文只展示一次</b>; 请存放到环境变量或密钥管理,
                        不要写进代码库/聊天记录. 泄漏时立即回来吊销.
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => setShowCreate(false)}
                            disabled={busy}
                        >
                            取消
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={onCreate}
                            disabled={busy}
                        >
                            {busy ? '创建中…' : '创建'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* 调用统计 */}
            <Modal
                open={stats !== null}
                onClose={() => setStats(null)}
                title={
                    stats
                        ? `调用统计 · ${stats.token.name} (${stats.token.tokenPrefix}****)`
                        : '调用统计'
                }
                width={640}
            >
                {stats && (
                    <div className="space-y-4">
                        <div>
                            <div className="text-xs font-semibold text-slate-500 mb-1.5">
                                近 7 天调用量 (限额 {stats.token.rateQps} QPS /{' '}
                                {stats.token.rateDaily.toLocaleString()} 每天)
                            </div>
                            <table className="w-full">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="th">日期</th>
                                        <th className="th">总量</th>
                                        <th className="th">成功 2xx</th>
                                        <th className="th">参数错 4xx</th>
                                        <th className="th">限流 429</th>
                                        <th className="th">异常 5xx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.stats.days.map((d) => (
                                        <tr
                                            key={d.date}
                                            className="hover:bg-slate-50"
                                        >
                                            <td className="td font-mono text-xs">
                                                {d.date.slice(4, 6)}-
                                                {d.date.slice(6)}
                                            </td>
                                            <td className="td text-xs">
                                                {d.total}
                                            </td>
                                            <td className="td text-xs">
                                                {d.byStatus['2xx'] || 0}
                                            </td>
                                            <td className="td text-xs">
                                                {d.byStatus['4xx'] || 0}
                                            </td>
                                            <td className="td text-xs">
                                                {d.byStatus['429'] ? (
                                                    <span className="text-amber-600 font-medium">
                                                        {d.byStatus['429']}
                                                    </span>
                                                ) : (
                                                    0
                                                )}
                                            </td>
                                            <td className="td text-xs">
                                                {d.byStatus['5xx'] ? (
                                                    <span className="text-rose-600 font-medium">
                                                        {d.byStatus['5xx']}
                                                    </span>
                                                ) : (
                                                    0
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-slate-500 mb-1.5">
                                今日接口分布
                            </div>
                            {Object.keys(stats.stats.todayPaths).length ===
                            0 ? (
                                <div className="text-xs text-slate-400">
                                    今日暂无调用
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {Object.entries(stats.stats.todayPaths)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([p, n]) => (
                                            <div
                                                key={p}
                                                className="flex justify-between text-xs"
                                            >
                                                <code className="font-mono text-slate-600">
                                                    {p}
                                                </code>
                                                <span>{n}</span>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-slate-500 mb-1.5">
                                今日来源 IP ({stats.stats.todayIps.length} 个)
                            </div>
                            {stats.stats.todayIps.length === 0 ? (
                                <div className="text-xs text-slate-400">—</div>
                            ) : (
                                <div className="flex flex-wrap gap-1.5">
                                    {stats.stats.todayIps.map((ip) => (
                                        <span
                                            key={ip}
                                            className="tag bg-slate-100 text-slate-600 font-mono text-[11px]"
                                        >
                                            {ip}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* 创建成功: 一次性展示明文 */}
            <Modal
                open={created !== null}
                onClose={() => setCreated(null)}
                title={
                    created?.status === 'pending'
                        ? 'Token 申请已提交'
                        : 'Token 创建成功'
                }
                width={560}
            >
                {created && (
                    <div className="space-y-4">
                        <div className="text-sm">
                            「{created.name}」的 token 如下,{' '}
                            <b className="text-rose-600">只显示这一次</b>,
                            请立即保存:
                        </div>
                        {created.status === 'pending' && (
                            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5">
                                令牌申请需审批 (工单 {created.taskCode}):
                                审批中心通过前调用会返回 40101, 通过后自动生效,
                                无需重新申请; 被驳回则作废.
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs font-mono bg-slate-50 border border-slate-200 rounded p-2.5 break-all select-all">
                                {created.token}
                            </code>
                            <button
                                type="button"
                                className="btn-primary shrink-0"
                                onClick={copyToken}
                            >
                                {copied ? '已复制 ✓' : '复制'}
                            </button>
                        </div>
                        <div className="text-xs text-slate-500 space-y-1.5">
                            <div>
                                环境 <b>{apiEnv.envLabel}</b> · 请求头
                                <code className="font-mono">
                                    {' '}
                                    Authorization: Bearer &lt;token&gt;
                                </code>
                            </div>
                            {apiEnv.sdkBaseURL && (
                                <>
                                    <div>
                                        直接发 HTTP:
                                        <code className="font-mono break-all">
                                            {' '}
                                            {apiEnv.httpBaseURL}/…
                                        </code>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <pre className="flex-1 font-mono text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto select-all">
                                            {sdkSnippet(
                                                apiEnv.sdkBaseURL,
                                                created.token,
                                            )}
                                        </pre>
                                        <button
                                            type="button"
                                            className="btn-ghost shrink-0"
                                            onClick={() =>
                                                copyText(
                                                    'snippet',
                                                    sdkSnippet(
                                                        apiEnv.sdkBaseURL,
                                                        created.token,
                                                    ),
                                                )
                                            }
                                        >
                                            {copiedField === 'snippet'
                                                ? '已复制 ✓'
                                                : '复制'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex justify-end pt-2 border-t border-slate-100">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => setCreated(null)}
                            >
                                我已保存, 关闭
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default TokenList;
